/* eslint-disable react/no-unknown-property */
import { createRef, FC, PropsWithChildren, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { SwiperSlide, useSwiper } from 'swiper/react'
import PdImage from '../pd-image/pd-image'
import PdSwiper, { Breakpoints } from '../pd-swiper/pd-swiper'

export type MediaItem = {
  src: string
  alt?: string
  type: 'IMG' | 'VIDEO'
  caption?: string
  link?: string
  enableMask?: boolean
}

export interface MediaSwiperSlideRenderProps extends MediaItem {
  isActive: boolean
  isPrev: boolean
  isNext: boolean
  isVisible: boolean
  isDuplicate: boolean
}

export interface PdMediaSwiperProps {
  data: Array<MediaItem>
  hideNavigation?: boolean
  hidePagination?: boolean
  slideClassName?: string
  darkPagination?: boolean
  slideChildren?: (props: MediaSwiperSlideRenderProps) => ReactNode
}

function isImageType(url: string): boolean {
  const validImageExtends: string[] = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.svg']
  return validImageExtends.some((extend) => url.includes(extend))
}

function isvideoType(url: string): boolean {
  const valideVideoExtends: string[] = ['.mp4']
  return valideVideoExtends.some((extend) => url.includes(extend))
}

const MediaSwiperSlideRender: FC<PropsWithChildren<MediaSwiperSlideRenderProps>> = ({
  children,
  src,
  type,
  link,
  enableMask = true,
  caption,
  alt,
  isActive,
  isPrev,
  isNext,
  isVisible,
  isDuplicate,
}) => {
  const swiper = useSwiper()
  const videoRef = createRef<HTMLVideoElement>()

  useEffect(() => {
    if (isActive) {
      if (type === 'IMG') {
        swiper.autoplay.start()
      }

      if (type === 'VIDEO') {
        swiper.autoplay.stop()
        // start to play
        videoRef.current?.play()
      }
    } else {
      if (type === 'VIDEO') {
        // stop play
        videoRef.current?.pause()
      }
    }
  }, [isActive, swiper, type, videoRef])

  return (
    <>
      {type === 'IMG' && (
        <PdImage key={src} src={src} alt={alt} enableMask={enableMask} width={'100%'} linkHref={link} object="cover">
          {children}
        </PdImage>
      )}

      {type === 'VIDEO' && (
        <div className="relative w-full h-full overflow-hidden">
          <video
            key={src}
            ref={videoRef}
            className="w-full h-full object-cover object-center"
            src={src}
            muted={true}
            webkit-playsinline="true"
            playsInline
            x-webkit-airplay="true"
            x5-video-player-type="h5"
            x5-video-player-fullscreen="true"
            onEnded={() => {
              if (isActive) {
                swiper.autoplay.start()
              }
            }}
          ></video>

          {children}
        </div>
      )}
    </>
  )
}

const PdMediaSwiper: FC<PropsWithChildren<PdMediaSwiperProps>> = ({
  data,
  children,
  slideChildren,
  hideNavigation = false,
  hidePagination = false,
  darkPagination,
  slideClassName = '!w-[1098px]',
}) => {
  const breakpoints: Breakpoints = {
    640: {
      slidesPerView: 'auto',
      spaceBetween: 8,
    },
    768: {
      slidesPerView: 'auto',
      spaceBetween: 8,
    },
    1024: {
      slidesPerView: 'auto',
      spaceBetween: 16,
    },
  }

  if (data.length < 1) {
    return null
  }

  return (
    <>
      <PdSwiper
        spaceBetween={8}
        slidesPerView={'auto'}
        breakpoints={breakpoints}
        autoplay={false}
        center={true}
        loop={true}
        hideNavigation={hideNavigation}
        hidePagination={hidePagination}
        paginationOptions={{
          el: darkPagination ? '.pd-pagination-container_black-media' : '.pd-pagination-container-media',
        }}
        navgationOptions={{
          navigationEl: 'pd-navigation customer-navigation',
          nextEl: 'pd-navigation-next',
          prevEl: 'pd-navigation-prev',
        }}
      >
        {data.map((item, index) => (
          <SwiperSlide key={item.src} className={[slideClassName, '!max-w-full'].join(' ')}>
            {({ isActive, isPrev, isNext, isVisible, isDuplicate }) => (
              <MediaSwiperSlideRender
                src={item.src}
                type={item.type}
                link={item.link}
                caption={item.caption}
                alt={item.alt}
                enableMask={item.enableMask}
                isActive={isActive}
                isPrev={isPrev}
                isNext={isNext}
                isVisible={isVisible}
                isDuplicate={isDuplicate}
              >
                {slideChildren && slideChildren({ ...item, isActive, isPrev, isNext, isVisible, isDuplicate })}
              </MediaSwiperSlideRender>
            )}
          </SwiperSlide>
        ))}
      </PdSwiper>

      <div
        className={[
          darkPagination ? 'pd-pagination-container_black-media' : 'pd-pagination-container-media',
          'absolute left-0 !bottom-6 z-10 ',
        ].join(' ')}
      ></div>
    </>
  )
}

export default PdMediaSwiper
